import React, { useState, useEffect } from 'react';
import { Trash2, Upload, LogOut, Plus, Calendar, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [date, setDate] = useState('');
  const [xLink, setXLink] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchPosts();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/news/admin/login`, {
        username,
        password
      });
      
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_username', response.data.username);
      setIsAuthenticated(true);
      toast.success('🍌 Logged in successfully!');
      fetchPosts();
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news/`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch posts');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !xLink || !screenshot) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('date', date);
    formData.append('x_link', xLink);
    formData.append('screenshot', screenshot);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/news/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('🍌 Post created successfully!');
      setDate('');
      setXLink('');
      setScreenshot(null);
      setPreviewUrl('');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/news/${postId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center px-4">
        <div className="glass rounded-3xl p-8 sm:p-12 max-w-md w-full border border-yellow-500/20">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black mb-2">
              <span className="text-[#32B44A]">$NANA</span> Admin
            </h1>
            <p className="text-slate-400">Login to manage news posts</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-[#141822] border border-yellow-500/20 focus:border-[#FFD600] focus:outline-none text-white"
                placeholder="admin_nana"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-[#141822] border border-yellow-500/20 focus:border-[#FFD600] focus:outline-none text-white"
                placeholder="••••••••"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#32B44A] hover:bg-[#28A745] text-white font-bold btn-press"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-black">
            <span className="text-[#32B44A]">$NANA</span> News Admin
          </h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Create Post Form */}
        <div className="glass rounded-3xl p-8 mb-8 border border-yellow-500/20">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <Plus size={24} className="mr-2 text-[#FFD600]" />
            Create New Post
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2 flex items-center">
                  <Calendar size={16} className="mr-2" />
                  Date
                </label>
                <input
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-[#141822] border border-yellow-500/20 focus:border-[#FFD600] focus:outline-none text-white"
                  placeholder="e.g., January 9, 2026"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 flex items-center">
                  <LinkIcon size={16} className="mr-2" />
                  X/Twitter Link
                </label>
                <input
                  type="url"
                  value={xLink}
                  onChange={(e) => setXLink(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-[#141822] border border-yellow-500/20 focus:border-[#FFD600] focus:outline-none text-white"
                  placeholder="https://x.com/NANA_Supra/status/..."
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 flex items-center">
                <Upload size={16} className="mr-2" />
                Screenshot
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-4 py-3 rounded-lg bg-[#141822] border border-yellow-500/20 focus:border-[#FFD600] focus:outline-none text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#32B44A] file:text-white file:cursor-pointer"
                required
              />
              {previewUrl && (
                <img src={previewUrl} alt="Preview" className="mt-4 max-w-xs rounded-lg border border-yellow-500/30" />
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="bg-[#32B44A] hover:bg-[#28A745] text-white font-bold btn-press"
            >
              {loading ? 'Creating...' : 'Create Post'}
            </Button>
          </form>
        </div>

        {/* Posts List */}
        <div className="glass rounded-3xl p-8 border border-yellow-500/20">
          <h2 className="text-2xl font-bold mb-6">All Posts ({posts.length})</h2>

          {posts.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No posts yet</p>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-[#141822] rounded-xl p-4 border border-yellow-500/10 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-4">
                    <img
                      src={`${API_URL}${post.screenshot_url}`}
                      alt="Post"
                      className="w-20 h-20 rounded-lg object-cover border border-yellow-500/30"
                    />
                    <div>
                      <p className="font-semibold">{post.date}</p>
                      <a
                        href={post.x_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#FFD600] hover:underline"
                      >
                        View on X
                      </a>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleDelete(post.id)}
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
